import DropdownField from '@payaca/components/dropdownField/DropdownField';
import { FC, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import DatePeriodControl from '@payaca/components/datePeriodControl/DatePeriodControl';

import { actions as usersActions } from '@/api/users';
import { useActiveAccountUsers } from '@/api/queries/me/useActiveAccountUsers';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { getTimelogTypes } from '@payaca/store/timelogs/timelogsActions';
import { GetListedTimelogsQueryParams } from '@payaca/types/listedTimelogTypes';
import { PublicTimelogType, TimelogAssignee } from '@payaca/types/timelogs';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import './TimelogFilters.sass';

type Props = {
  disableFilters?: ('periodStart' | 'periodEnd')[];
  appliedFilters: Pick<
    GetListedTimelogsQueryParams,
    'periodStart' | 'periodEnd' | 'assignees' | 'typePublicIds'
  >;
  onChange: (filters: Partial<GetListedTimelogsQueryParams>) => void;
};

const TimelogFilters: FC<Props> = ({
  disableFilters,
  appliedFilters,
  onChange,
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const [timelogTypes, setTimelogTypes] = useState<PublicTimelogType[]>([]);

  const accountUsers = useActiveAccountUsers();

  useEffect(() => {
    dispatch(usersActions.getAccountUsers());
    dispatch(
      getTimelogTypes.request({
        callback: (tt) => {
          setTimelogTypes(tt);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const userAssigneeOptions = useMemo(() => {
    const options: {
      label: string;
      value: {
        publicId: TimelogAssignee['publicId'];
        type: TimelogAssignee['type'];
      };
    }[] = accountUsers.map((accountUser: any) => {
      return {
        label: `${accountUser.firstname} ${accountUser.lastname}`,
        value: { publicId: accountUser.id.toString(), type: 'user' },
      };
    });

    return options;
  }, [accountUsers]);

  return (
    <div className="timelog-filters">
      {!disableFilters?.includes('periodStart') &&
        !disableFilters?.includes('periodEnd') && (
          <DatePeriodControl
            label="Date period"
            value={{
              periodStart: appliedFilters.periodStart,
              periodEnd: appliedFilters.periodEnd,
            }}
            onChange={onChange}
          />
        )}
      <PermissionGuard
        renderIfHasPermissions={[TimelogsPermissions.GET_TIMELOG_TYPES]}
      >
        <DropdownField
          name="typePublicIds"
          label="Type"
          multiple
          options={timelogTypes.map((timelogType) => {
            return {
              label: timelogType.type,
              value: timelogType.publicId,
            };
          })}
          value={appliedFilters.typePublicIds || []}
          onChange={onChange}
          displayCheckboxes={true}
          emptyText={'Any type'}
        />
      </PermissionGuard>
      <PermissionGuard
        renderIfHasPermissions={[TimelogsPermissions.GET_TIMELOGS]}
      >
        <DropdownField
          name="assignees"
          label="Assignee"
          multiple
          options={userAssigneeOptions}
          value={appliedFilters.assignees || []}
          onChange={onChange}
          displayCheckboxes={true}
          emptyText={'Any assignee'}
        />
      </PermissionGuard>
    </div>
  );
};

export default TimelogFilters;
